import { uniqBy } from 'lodash';
import { MINIMUM_LTV_FOR_GAP, STATE_GAP_CALCULATION_RULES } from '../constants';
import { Dealer } from '../legos/dealerTypes';
import {
  FinanceResponse,
  Rate,
  UpstartLendingStates,
} from '../legos/financingTypes';
import {
  ProductCategory,
  SelectedProductWithFinalPrice,
} from '../legos/penTypes';
import {
  getDownPaymentAmountLimits,
  getFinanceAmountLimits,
} from './limitHelpers';
import { parseRates } from './parseRates';

type ValidateLimitsInput = { financeResponse: FinanceResponse; rates?: Rate[] };
type ValidateLimitsResult = {
  maxFinanceAmountLimit: number;
  minFinanceAmountLimit: number;
  /** @deprecated use isFinanceAmountOverLimit instead */
  isOverLimit: boolean;
  /** @deprecated use isFinanceAmountUnderLimit instead */
  isUnderLimit: boolean;
  isFinanceAmountOverLimit: boolean;
  isFinanceAmountUnderLimit: boolean;
  isFinanceAmountInvalid: boolean;
  isBackendOverLimit: boolean;
  isDownPaymentOverLimit: boolean;
  isDownPaymentUnderLimit: boolean;
  isDownPaymentInvalid: boolean;
  isDownPaymentInvalidForAllRates: boolean;
  isInvalid: boolean;
  hasDuplicateTradeIn: boolean;
  isMarkupBpsOverLimit: boolean;
};
export function validateLimits({
  financeResponse,
  rates,
}: ValidateLimitsInput): ValidateLimitsResult {
  const financeAmount = financeResponse.data?.financeAmount ?? 0;
  const downPayment = financeResponse.data?.downPayment ?? 0;

  const allRates = rates ?? financeResponse.data?.rates ?? [];
  const { currentRate, financeAmountRates, downPaymentRates } = parseRates({
    rates: allRates,
    downPayment,
    financeAmount,
    months: financeResponse.data?.months,
  });

  const tradeIns = financeResponse.data?.tradeIns ?? [];
  // Duplicate tradein VINs should not necessarily affect isInvalid flag below.
  const tradeInsWithVins = tradeIns.filter(t => t.id);
  const hasDuplicateTradeIn =
    uniqBy(tradeInsWithVins, 'id').length !== tradeInsWithVins.length;

  const [minFinanceAmountLimit, maxFinanceAmountLimit] = getFinanceAmountLimits(
    financeAmountRates,
  );
  const isFinanceAmountOverLimit = financeAmount > maxFinanceAmountLimit;
  const isFinanceAmountUnderLimit = financeAmount < minFinanceAmountLimit;

  const isBackendOverLimit = !!(
    financeResponse.calculatedBackendAmount &&
    currentRate?.maxBackendAmount &&
    financeResponse.calculatedBackendAmount > currentRate.maxBackendAmount
  );
  const isFinanceAmountInvalid =
    isFinanceAmountOverLimit || isFinanceAmountUnderLimit || !currentRate?.apr;

  const [minDownPaymentLimit, maxDownPaymentLimit] = getDownPaymentAmountLimits(
    downPaymentRates.length === 0
      ? // If there are no relevant down payment bands, then determine the min/max down payment from all rates
        allRates
      : downPaymentRates,
  );
  const isDownPaymentOverLimit = downPayment > maxDownPaymentLimit;
  const isDownPaymentUnderLimit = downPayment < minDownPaymentLimit;
  const isDownPaymentInvalid =
    isDownPaymentOverLimit || isDownPaymentUnderLimit;

  // Have a separate flag when Down Payment is not valid for any rates
  // This is wider than the above flag, which is only determing validity based
  // on the current finance amount and term.
  const [
    minDownPaymentLimitForAllRates,
    maxDownPaymentLimitForAllRates,
  ] = getDownPaymentAmountLimits(allRates);
  const isDownPaymentInvalidForAllRates =
    downPayment < minDownPaymentLimitForAllRates ||
    downPayment > maxDownPaymentLimitForAllRates;

  // check that the markupBps is not greater than the maximum allowed for the current rate
  const markupMaxBps = currentRate?.markupMaxBps ?? 0;
  const isMarkupBpsOverLimit =
    financeResponse.data?.markupBps !== undefined &&
    financeResponse.data.markupBps > markupMaxBps;

  const isInvalid =
    isFinanceAmountInvalid ||
    isDownPaymentInvalid ||
    isBackendOverLimit ||
    isMarkupBpsOverLimit;

  return {
    maxFinanceAmountLimit,
    minFinanceAmountLimit,
    isOverLimit: isFinanceAmountOverLimit,
    isUnderLimit: isFinanceAmountUnderLimit,
    isFinanceAmountOverLimit,
    isFinanceAmountUnderLimit,
    isBackendOverLimit,
    isFinanceAmountInvalid,
    isDownPaymentOverLimit,
    isDownPaymentUnderLimit,
    isDownPaymentInvalid,
    isDownPaymentInvalidForAllRates,
    isInvalid,
    hasDuplicateTradeIn,
    isMarkupBpsOverLimit,
  };
}

type ValidateGapPremiumInput = {
  financeResponse: FinanceResponse;
  selectedProducts: SelectedProductWithFinalPrice[];
  dealer?: Dealer | null;
};
type ValidateGapPremiumResult = {
  /** Some states deny credit apps below a certain LTV if containing a GAP contract */
  isLTVBelowMinimumForGap: boolean;
  /** Some states may deny credit apps if GAP premium is over a financed amount */
  isGapPremiumOverLimit: boolean;
  /** Aggregate flag of all gap premium validation */
  isInvalid: boolean;
  /** The maximum GAP premium, based on the finance amount value. */
  maxGapPremium?: number;
};

export function validateGapPremium({
  financeResponse,
  selectedProducts,
  dealer,
}: ValidateGapPremiumInput): ValidateGapPremiumResult {
  const financeAmount = financeResponse.data?.financeAmount ?? 0;
  const assetValue = financeResponse?.data?.assetValue;

  let gapProductPrice = 0;
  if (financeResponse.data?.finalizedProductList) {
    const finalProduct = financeResponse.data.finalizedProductList.find(
      p => p.category === ProductCategory.GAP,
    );
    gapProductPrice = finalProduct?.value ?? 0;
  } else {
    const selectedGAPProduct = selectedProducts.find(sp => {
      const penProduct = dealer?.penProducts?.find(
        pp => pp.id === sp.productId,
      );
      return penProduct?.productCategory === ProductCategory.GAP;
    });
    gapProductPrice = selectedGAPProduct?.finalPrice ?? 0;
  }

  let isLTVBelowMinimumForGap = false;
  let maxGapPremium;
  let isGapPremiumOverLimit = false;
  const dealerState: UpstartLendingStates = dealer?.state as UpstartLendingStates;

  // Only set gap premium related errors if relevant fields exist
  if (gapProductPrice > 0 && assetValue && assetValue > 0 && dealer?.state) {
    const minimumLTVForGap = MINIMUM_LTV_FOR_GAP[dealerState] ?? null;
    const stateRule = STATE_GAP_CALCULATION_RULES[dealerState];

    isLTVBelowMinimumForGap = minimumLTVForGap
      ? financeAmount / assetValue < minimumLTVForGap
      : false;

    if (stateRule) {
      const amountWithoutGap = financeAmount - gapProductPrice;

      switch (stateRule.type) {
        case 'percentage':
          maxGapPremium = stateRule.percentage
            ? amountWithoutGap * stateRule.percentage
            : undefined;
          break;
        case 'fixed':
          maxGapPremium = stateRule.fixedAmount;
          break;
        case 'hybrid':
          if (
            typeof stateRule.percentage === 'number' &&
            typeof stateRule.fixedAmount === 'number'
          ) {
            const percentageAmount = amountWithoutGap * stateRule.percentage;
            maxGapPremium = Math.max(percentageAmount, stateRule.fixedAmount);
          }
          break;
        default:
          maxGapPremium = undefined;
      }
    } else {
      // If no state rule is defined, no GAP premium limit is enforced
      maxGapPremium = undefined;
    }

    isGapPremiumOverLimit = maxGapPremium
      ? gapProductPrice > maxGapPremium
      : false;
  }

  const isInvalid = isLTVBelowMinimumForGap || isGapPremiumOverLimit;

  return {
    isLTVBelowMinimumForGap,
    isGapPremiumOverLimit,
    isInvalid,
    maxGapPremium,
  };
}

export type ValidateResponseResult = ValidateLimitsResult &
  ValidateGapPremiumResult;
/**
 * Aggregate of validation for finance response limits and gap premium limits
 */
export function validateResponse(
  input: ValidateLimitsInput & ValidateGapPremiumInput,
): ValidateResponseResult {
  const limitValidation = validateLimits(input);
  const gapPremiumValidation = validateGapPremium(input);

  return {
    ...limitValidation,
    ...gapPremiumValidation,
    isInvalid: limitValidation.isInvalid || gapPremiumValidation.isInvalid,
  };
}
